import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { XdsButtonComponent } from 'src/app/shared/ui';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth/auth.service';
import { REGISTRATION_ROUTE, leaveToLiveRegistration } from '../../services/demo/registration-target';

/**
 * Blockierender Dialog, wenn der 48-Stunden-Demo-Zugang abgelaufen ist (Handover / 48h-Modell).
 * Der Nutzer wird aktiv zur Registrierung bzw. zu einem Demo-Termin geführt. Wird mit
 * `disableClose: true` geöffnet — kein Wegklicken; Auswege sind Registrieren / Termin / Login.
 */
@Component({
  selector: 'app-demo-expired-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, XdsButtonComponent],
  template: `
    <div class="demo-expired">
      <div class="demo-expired-icon"><i class="fa-solid fa-hourglass-end"></i></div>
      <h2 class="demo-expired-title">{{ 'DEMO.EXPIRED_TITLE' | translate }}</h2>
      <p class="demo-expired-text">{{ 'DEMO.EXPIRED_TEXT' | translate }}</p>

      <div class="demo-expired-actions">
        <xds-button variant="primary" [block]="true" iconLeft="fa-solid fa-user-plus" (clicked)="onRegister()">
          {{ 'DEMO.EXPIRED_REGISTER' | translate }}
        </xds-button>

        <xds-button
          *ngIf="bookingUrl"
          variant="secondary"
          [block]="true"
          iconLeft="fa-solid fa-calendar-check"
          (clicked)="onBookDemo()"
        >
          {{ 'DEMO.EXPIRED_BOOK_DEMO' | translate }}
        </xds-button>

        <xds-button variant="ghost" [block]="true" (clicked)="onBackToLogin()">
          {{ 'DEMO.EXPIRED_BACK' | translate }}
        </xds-button>
      </div>
    </div>
  `,
  styles: [`
    .demo-expired { text-align: center; padding: 8px 4px; }
    .demo-expired-icon {
      width: 56px; height: 56px; margin: 0 auto 16px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%; background: rgba(8, 145, 178, 0.12);
      color: #0891b2; font-size: 24px;
    }
    .demo-expired-title { margin: 0 0 8px; font-size: 20px; font-weight: 700; }
    .demo-expired-text { margin: 0 0 20px; color: var(--xds-text-secondary, #4b5563); line-height: 1.5; }
    .demo-expired-actions { display: flex; flex-direction: column; gap: 10px; }
  `],
})
export class DemoExpiredDialogComponent {
  /** Buchungs-URL (z. B. Calendly); leer → Button ausgeblendet. */
  readonly bookingUrl: string = environment.demoBookingUrl || '';

  constructor(
    private dialogRef: MatDialogRef<DemoExpiredDialogComponent>,
    private router: Router,
    private authService: AuthService
  ) {}

  onRegister(): void {
    // Im Demo-Build cross-domain auf die Live-Registrierung; sonst interne Route.
    if (leaveToLiveRegistration()) {
      return;
    }
    this.clearAndClose();
    this.router.navigateByUrl(REGISTRATION_ROUTE);
  }

  onBookDemo(): void {
    if (this.bookingUrl) {
      window.open(this.bookingUrl, '_blank', 'noopener');
    }
  }

  onBackToLogin(): void {
    this.clearAndClose();
    this.router.navigateByUrl('/login');
  }

  private clearAndClose(): void {
    // Abgelaufene Demo-Session verwerfen (Token + Demo-Flags), dann Dialog schließen.
    this.authService.clearLocalState();
    this.dialogRef.close();
  }
}
